import { useCallback, useRef, useState } from 'react';
import { useTranslations } from '@components/context';
import FlowStep from '@components/FlowStep';
import Tooltip from '@components/core/Tooltip';
import Menu from '@components/core/Menu';
import Dropdown from '@components/Dropdown';
import MetaButton from '@components/core/MetaButton';
import DatepickerV2 from '@components/core/DatepickerV2';
import CustomLeadTimePopover from '@components/RecruitmentFlowEditor/CustomLeadTimePopover';
import {
    RECRUITMENT_FLOW_MENU_CLASS,
    RECRUITMENT_FLOW_PRESET_DAY_OPTIONS as PRESET_DAY_OPTIONS,
    RECRUITMENT_FLOW_STEP_ICON,
    RECRUITMENT_FLOW_VARIANT,
} from './constants';

const formatDeadline = (deadline) => {
    if (!deadline) {
        return '';
    }
    return window.iso_in_local(deadline, 'DD.MM.YYYY HH.mm');
};

function deadlineToUtcIso(datetimeFromPicker) {
    if (!datetimeFromPicker) {
        return null;
    }
    return window.in_utc_as_iso(datetimeFromPicker);
}

/**
 * @param {'settings'|'position'} props.variant - settings: company flow editor; position: job/plan editor with deadline
 */
const RecruitmentFlowStepContainer = ({
    variant,
    metadata,
    step,
    onChange,
    onRemove,
    readonly,
    allowDragAndDrop,
    onOpenVideoStepEditor,
    savedStepIds,
    className,
    dragHandleRef,
    dragging,
    dropTarget,
    renderStepMeta,
    body,
    isFirstStep,
}) => {
    const isPosition = variant === RECRUITMENT_FLOW_VARIANT.POSITION;

    const {
        id,
        i18n,
        deadline,
        target_lead_time,
        count,
    } = step || {};

    const { t } = useTranslations();
    const leadTimeTriggerRef = useRef(null);
    const [customLeadTimeOpen, setCustomLeadTimeOpen] = useState(false);
    const [leadTimeMenuOpen, setLeadTimeMenuOpen] = useState(false);
    const [deadlineMenuOpen, setDeadlineMenuOpen] = useState(false);

    const isCustomLeadTime = target_lead_time
        && !PRESET_DAY_OPTIONS.includes(Number(target_lead_time));

    const datepickerConfig = {
        clearable: true,
        clearLabel: t('Clear'),
    };

    const applyChanges = (data) => {
        const i18nData = { ...(i18n || {}), ...(data.i18n || {}) };
        const planData = isPosition ? { deadline, target_lead_time } : { target_lead_time };
        const updatedData = { id, ...planData, ...data, i18n: i18nData };
        onChange(updatedData);
    };

    const handleLeadTimeSelect = (days) => {
        applyChanges({ target_lead_time: days });
    };

    const handleCustomLeadTimeApply = (days) => {
        handleLeadTimeSelect(days);
        setCustomLeadTimeOpen(false);
    };

    const handleCustomLeadTimeOpen = () => {
        setCustomLeadTimeOpen(true);
    };

    const handleDeadlineChange = (datetime) => {
        const deadlineIso = deadlineToUtcIso(datetime);
        applyChanges({ deadline: deadlineIso });
    };

    const showRemove = !readonly && (isPosition ? !count : true) && typeof onRemove === 'function';

    const renderLeadTimeTrigger = () => (
        <>
            <Menu
                align="left"
                disabled={readonly}
                menuClassName={RECRUITMENT_FLOW_MENU_CLASS.LEAD_TIME}
                onToggle={setLeadTimeMenuOpen}
                trigger={(
                    <MetaButton
                        ref={leadTimeTriggerRef}
                        icon={RECRUITMENT_FLOW_STEP_ICON.LEAD_TIME}
                        placeholder={!target_lead_time}
                        active={leadTimeMenuOpen}
                        data-action={`${id}-leadtime-container`}
                    >
                        {target_lead_time ? `${target_lead_time} ${t('days')}` : t('Select target lead time')}
                    </MetaButton>
                )}
                items={[
                    target_lead_time && {
                        id: 'clear',
                        label: t('No target lead time'),
                        dataAttributes: [['data-action', `${id}-leadtime-clear`], ['data-value', 'clear']],
                        onSelect: () => handleLeadTimeSelect(0),
                    },
                    ...PRESET_DAY_OPTIONS.map((days) => ({
                        id: days,
                        label: `${days} ${t('days')}`,
                        selected: Number(target_lead_time) === days,
                        dataAttributes: [['data-action', `${id}-leadtime-${days}`], ['data-value', days]],
                        onSelect: () => handleLeadTimeSelect(days),
                    })),
                    {
                        id: 'custom',
                        label: t('Custom'),
                        selected: Boolean(isCustomLeadTime),
                        dataAttributes: [['data-action', `${id}-leadtime-custom`], ['data-value', 'custom']],
                        onSelect: handleCustomLeadTimeOpen,
                    },
                ]}
            />
            {customLeadTimeOpen ? (
                <CustomLeadTimePopover
                    anchorRef={leadTimeTriggerRef}
                    initialValue={isCustomLeadTime ? target_lead_time : ''}
                    onApply={handleCustomLeadTimeApply}
                    onCancel={() => setCustomLeadTimeOpen(false)}
                />
            ) : null}
        </>
    );

    // The deadline is a calendar, not a list, so it stays on the plain dropdown.
    const renderDeadlineTrigger = () => (
        <Dropdown
            align="left"
            disabled={readonly}
            onToggle={setDeadlineMenuOpen}
            trigger={(
                <MetaButton
                    icon={RECRUITMENT_FLOW_STEP_ICON.DEADLINE}
                    placeholder={!deadline}
                    active={deadlineMenuOpen}
                    data-action={`${id}-deadline-container`}
                >
                    {deadline ? (
                        <Tooltip title={t('deadline')}>
                            <span>{formatDeadline(deadline)}</span>
                        </Tooltip>
                    ) : (
                        t('Select deadline')
                    )}
                </MetaButton>
            )}
            menuClassName={RECRUITMENT_FLOW_MENU_CLASS.DEADLINE}
        >
            <li className="recruitment-flow-deadline-menu__item">
                <DatepickerV2 show value={deadline} onChange={handleDeadlineChange} config={datepickerConfig} />
            </li>
        </Dropdown>
    );

    const handleRemoveHandler = useCallback((e) => {
        e.stopPropagation();
        onRemove(id);
    }, [onRemove, id]);

    return (
        <FlowStep
            metadata={metadata}
            step={step}
            readonly={readonly}
            onOpenVideoStepEditor={onOpenVideoStepEditor}
            savedStepIds={savedStepIds}
            className={className}
            allowDragAndDrop={allowDragAndDrop}
            dragHandleRef={dragHandleRef}
            dragging={dragging}
            dropTarget={dropTarget}
            body={body}
            onChange={onChange}
            onUpdateStep={onChange}
            onRemove={handleRemoveHandler}
            showRemove={showRemove}
            isFirstStep={isFirstStep}
        >
            <div className="recruitment-flow-plan-editor-row">
                {renderLeadTimeTrigger()}
                {isPosition && renderDeadlineTrigger()}
                {renderStepMeta?.(id) || null}
            </div>
        </FlowStep>
    );
};

export default RecruitmentFlowStepContainer;
